import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { fetchAdminAuditLog, fetchAdminAuditLogs, type AdminAuditRow } from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function AuditDetailCard({ logId, onClose }: { logId: string | null; onClose: () => void }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-audit-log', logId],
    queryFn: () => fetchAdminAuditLog(logId!),
    enabled: Boolean(logId),
  });
  const row = data?.log;

  return (
    <AdminDetailCard open={Boolean(logId)} onClose={onClose} ariaLabel="Audit log details" breadcrumb="Audit event" title={row ? `${row.action} · ${row.module}` : 'Loading…'} illustration="admin.panel.audit"
      subtitle={row ? (<><span className="chip-pill">{row.userName}</span>{row.userRole && <AdminStatusBadge status={row.userRole} />}</>) : undefined}>
      {isLoading && <LoadingState label="Loading audit log…" />}
      {isError && <ErrorState message="Could not load audit log." onRetry={() => void refetch()} />}
      {row && (
        <>
          {row.details && <p className="wfx-admin-detail-message">{row.details}</p>}
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>User</dt><dd>{row.userName}{row.userRole ? ` (${row.userRole.replace(/_/g, ' ')})` : ''}</dd></div>
            <div><dt>Module</dt><dd>{row.module}</dd></div>
            <div><dt>Action</dt><dd>{row.action}</dd></div>
            <div><dt>Resource</dt><dd>{row.resourceId ?? '—'}</dd></div>
            <div><dt>IP address</dt><dd>{row.ipAddress ?? '—'}</dd></div>
            <div><dt>Timestamp</dt><dd>{new Date(row.createdAt).toLocaleString()}</dd></div>
          </dl>
        </>
      )}
    </AdminDetailCard>
  );
}

function AuditCard({ row, onSelect }: { row: AdminAuditRow; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-audit-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head"><AdminStatusBadge status={row.action} /><span className="wfx-muted">{row.module}</span></div>
      <strong>{row.userName}</strong>
      <span className="wfx-muted">{row.details?.slice(0, 90) ?? 'No details recorded'}{row.details && row.details.length > 90 ? '…' : ''}</span>
      <div className="wfx-admin-page-card-stats"><span>{new Date(row.createdAt).toLocaleString()}</span>{row.ipAddress && <span>{row.ipAddress}</span>}</div>
    </button>
  );
}

export function AdminAuditScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300); return () => window.clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [debouncedQuery, moduleFilter, actionFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-audit', debouncedQuery, moduleFilter, actionFilter, page],
    queryFn: () => fetchAdminAuditLogs({ q: debouncedQuery || undefined, module: moduleFilter, action: actionFilter, page, limit: 12 }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const modules = data?.modules ?? [];
  const actions = data?.actions ?? [];
  const pagination = data?.pagination;
  const kpiItems = useMemo(() => [
    { label: 'Total events', value: stats?.total ?? 0 },
    { label: 'Today', value: stats?.today ?? 0 },
    { label: 'Modules', value: stats?.modules ?? 0 },
    { label: 'Actors', value: stats?.actors ?? 0 },
  ], [stats]);

  if (isLoading && !data) return <LoadingState label="Loading audit logs…" />;
  if (isError) return <ErrorState message="Could not load audit logs." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout breadcrumb="Admin / Audit Logs" title="Audit Logs" description="Review administrative actions across results, content, users, and platform settings." icon={ScrollText} illustration="admin.panel.audit">
        <AdminKpiStrip items={kpiItems} />
        <div className="wfx-admin-blog-grid">{rows.map((row) => <AuditCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />)}</div>
        {rows.length === 0 && <AdminEmptyState message="No audit events match your filters." />}
        <AdminTablePanel title="Activity log" toolbar={(
          <div className="wfx-admin-toolbar">
            <AdminSearchInput value={query} onChange={setQuery} placeholder="Search user, module, or details…" />
            <select className="wfx-admin-select" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} aria-label="Filter by module">
              <option value="all">All modules</option>{modules.map((mod) => <option key={mod} value={mod}>{mod}</option>)}
            </select>
            <select className="wfx-admin-select" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} aria-label="Filter by action">
              <option value="all">All actions</option>{actions.map((action) => <option key={action} value={action}>{action}</option>)}
            </select>
          </div>
        )} footer={pagination && pagination.pages > 1 ? (
          <div className="wfx-admin-pagination">
            <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="wfx-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} events</span>
            <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        ) : undefined}>
          <table className="wfx-admin-table"><thead><tr><th>User</th><th>Action</th><th>Module</th><th>Details</th><th>Time</th><th>IP</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id} className="wfx-admin-table-row-clickable" onClick={() => setSelectedId(row.id)}>
                <td><strong>{row.userName}</strong></td><td>{row.action}</td><td>{row.module}</td><td>{row.details?.slice(0, 60) ?? '—'}</td><td>{new Date(row.createdAt).toLocaleString()}</td><td>{row.ipAddress ?? '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>
      <AuditDetailCard logId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}
